import React, { useState } from 'react';
import { useCreateCompany } from '../../hooks/useCompany';
import { validateRut } from '../../utils/rutValidator';
import type { SubscriptionPlan } from '../../types';
import { PLANS, formatUsersDisplay, formatExpensesDisplay } from '../../config/plans';

interface CreateCompanyFormProps {
  onClose: () => void;
}

const CreateCompanyForm: React.FC<CreateCompanyFormProps> = ({ onClose }) => {
  const [name, setName] = useState('');
  const [rut, setRut] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [plan, setPlan] = useState<SubscriptionPlan>('starter');
  const [portalEnabled, setPortalEnabled] = useState(false);
  const [customSubdomain, setCustomSubdomain] = useState('');
  const [subdomainError, setSubdomainError] = useState('');

  const [nameError, setNameError] = useState('');
  const [rutError, setRutError] = useState('');
  const [emailError, setEmailError] = useState('');

  const createCompany = useCreateCompany();
  const selectedPlan = PLANS[plan];

  const handleRutChange = (value: string) => {
    setRut(value);
    if (!value.trim()) {
      setRutError('');
      return;
    }
    const result = validateRut(value);
    setRutError(result.valid ? '' : (result.error ?? 'RUT inválido'));
  };

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleEmailChange = (value: string) => {
    setContactEmail(value);
    if (!value.trim()) {
      setEmailError('');
      return;
    }
    setEmailError(validateEmail(value) ? '' : 'El correo de contacto no tiene un formato válido');
  };

  const validateSubdomain = (value: string): string => {
    if (!value.trim()) return '';
    const clean = value.toLowerCase().trim();
    const reserved = ['app', 'admin', 'api', 'www', 'mail', 'ftp', 'app-dev', 'admin-dev', 'api-dev'];
    if (reserved.includes(clean)) return 'Este subdominio es un nombre reservado';
    if (clean.length < 2) return 'Mínimo 2 caracteres';
    if (clean.length > 50) return 'Máximo 50 caracteres';
    if (!/^[a-z0-9-]+$/.test(clean)) return 'Solo letras, números y guiones';
    if (clean.startsWith('-') || clean.endsWith('-')) return 'No puede empezar ni terminar con guión';
    return '';
  };

  const handleSubdomainChange = (value: string) => {
    const clean = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setCustomSubdomain(clean);
    setSubdomainError(portalEnabled ? validateSubdomain(clean) : '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError('');
    setRutError('');
    setEmailError('');

    let hasError = false;

    if (!name.trim()) {
      setNameError('La razón social no puede estar vacía');
      hasError = true;
    }

    if (!rut.trim()) {
      setRutError('El RUT es obligatorio');
      hasError = true;
    } else {
      const rutResult = validateRut(rut);
      if (!rutResult.valid) {
        setRutError(rutResult.error ?? 'RUT inválido');
        hasError = true;
      }
    }

    if (contactEmail.trim() && !validateEmail(contactEmail)) {
      setEmailError('El correo de contacto no tiene un formato válido');
      hasError = true;
    }

    if (portalEnabled) {
      if (!customSubdomain.trim()) {
        setSubdomainError('El subdominio es obligatorio si habilitas el portal');
        hasError = true;
      } else {
        const subErr = validateSubdomain(customSubdomain);
        if (subErr) {
          setSubdomainError(subErr);
          hasError = true;
        }
      }
    }

    if (hasError) return;

    createCompany.mutate(
      {
        name: name.trim(),
        rut: rut.trim(),
        tradeName: tradeName.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        subscriptionPlan: plan,
        settings: portalEnabled ? {
          portalEnabled: true,
          customSubdomain: customSubdomain.trim(),
        } as any : undefined,
      },
      { onSuccess: () => onClose() }
    );
  };

  const inputClass = (error: string) =>
    `mt-1 block w-full rounded-md shadow-sm text-sm ${
      error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
    }`;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h2 id="modal-title" className="text-lg font-semibold text-gray-900 mb-4">Crear Empresa</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* RUT */}
            <div>
              <label htmlFor="company-rut" className="block text-sm font-medium text-gray-700">
                RUT de Empresa
              </label>
              <input
                id="company-rut"
                type="text"
                value={rut}
                onChange={(e) => handleRutChange(e.target.value)}
                className={inputClass(rutError)}
                placeholder="76.543.210-K"
              />
              {rutError && <p className="mt-1 text-sm text-red-600">{rutError}</p>}
            </div>

            {/* Razón Social */}
            <div>
              <label htmlFor="company-name" className="block text-sm font-medium text-gray-700">
                Razón Social
              </label>
              <input
                id="company-name"
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setNameError(''); }}
                className={inputClass(nameError)}
                placeholder="Razón social de la empresa"
              />
              {nameError && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
            </div>

            {/* Nombre de Fantasía */}
            <div>
              <label htmlFor="trade-name" className="block text-sm font-medium text-gray-700">
                Nombre de Fantasía
              </label>
              <input
                id="trade-name"
                type="text"
                value={tradeName}
                onChange={(e) => setTradeName(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                placeholder="Nombre comercial (opcional)"
              />
            </div>

            {/* Correo de contacto */}
            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700">
                Correo de Contacto
              </label>
              <input
                id="contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => handleEmailChange(e.target.value)}
                className={inputClass(emailError)}
                placeholder="contacto@empresa.cl (opcional)"
              />
              {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
            </div>

            {/* Teléfono de contacto */}
            <div>
              <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700">
                Teléfono de Contacto
              </label>
              <input
                id="contact-phone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                placeholder="+56 9 1234 5678 (opcional)"
              />
            </div>

            {/* Portal Personalizado */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  id="portal-enabled"
                  type="checkbox"
                  checked={portalEnabled}
                  onChange={(e) => {
                    setPortalEnabled(e.target.checked);
                    if (!e.target.checked) {
                      setCustomSubdomain('');
                      setSubdomainError('');
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="portal-enabled" className="text-sm font-medium text-gray-700">
                  Habilitar portal personalizado
                </label>
              </div>
              {portalEnabled && (
                <div>
                  <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700">
                    Subdominio
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      id="subdomain"
                      type="text"
                      value={customSubdomain}
                      onChange={(e) => handleSubdomainChange(e.target.value)}
                      className={`block w-full rounded-l-md text-sm ${
                        subdomainError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                      }`}
                      placeholder="mi-empresa"
                    />
                    <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">
                      .kessan.cl
                    </span>
                  </div>
                  {subdomainError && <p className="mt-1 text-sm text-red-600">{subdomainError}</p>}
                  {customSubdomain && !subdomainError && (
                    <p className="mt-1 text-sm text-gray-500">
                      URL: <span className="font-medium">{customSubdomain}.kessan.cl</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Plan Selector */}
            <div>
              <label htmlFor="plan-select" className="block text-sm font-medium text-gray-700">
                Plan de Suscripción
              </label>
              <select
                id="plan-select"
                value={plan}
                onChange={(e) => setPlan(e.target.value as SubscriptionPlan)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
              >
                <option value="starter">Starter - {PLANS.starter.priceDisplay}/mes</option>
                <option value="business">Business - {PLANS.business.priceDisplay}/mes</option>
                <option value="enterprise">Enterprise - {PLANS.enterprise.priceDisplay}/mes</option>
              </select>
            </div>

            {/* Plan Summary */}
            <div className="bg-gray-50 rounded-md p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Resumen del Plan: {selectedPlan.label}</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>Usuarios: hasta {formatUsersDisplay(plan)}</li>
                <li>Gastos mensuales: {formatExpensesDisplay(plan)}</li>
                <li>Precio: {selectedPlan.priceDisplay} CLP/mes</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createCompany.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {createCompany.isLoading ? 'Creando...' : 'Crear Empresa'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateCompanyForm;
